import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BadgeCheck, X } from 'lucide-react';
type Buyer = {
  name: string;
  city: string;
  offer: string;
  minutesAgo: number;
};
const NAMES = ['María González', 'Lucía Fernández', 'Sofía Rodríguez', 'Carla Giménez', 'Valentina López', 'Rosa Martínez', 'Jorge Sosa', 'Daniela Romero', 'Marcela Díaz', 'Gabriela Torres', 'Patricia Ramírez', 'Silvia Acosta', 'Verónica Suárez', 'Andrea Molina', 'Claudia Herrera', 'Mónica Benítez', 'Laura Ferreyra', 'Natalia Ríos', 'Cristina Ojeda', 'Analía Vega'];
const CITIES = ['Buenos Aires', 'Córdoba', 'Rosario, Santa Fe', 'Mendoza', 'La Plata', 'Mar del Plata', 'San Miguel de Tucumán', 'Salta', 'Neuquén', 'Resistencia, Chaco', 'Posadas, Misiones', 'Bahía Blanca', 'San Juan', 'Santa Fe', 'Corrientes', 'Paraná, Entre Ríos', 'Quilmes', 'Lanús', 'San Salvador de Jujuy', 'Comodoro Rivadavia'];
const OFFERS = ['el Mega Pack del Cuidador Profesional', 'el Mega Pack + 6 Bonos Exclusivos'];
const randomFrom = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
const buildBuyer = (): Buyer => ({
  name: randomFrom(NAMES),
  city: randomFrom(CITIES),
  offer: randomFrom(OFFERS),
  minutesAgo: Math.floor(Math.random() * 12) + 2
});
export function PurchaseNotification() {
  const [buyer, setBuyer] = useState<Buyer | null>(null);
  const showNext = useCallback(() => {
    setBuyer(buildBuyer());
    // Keep the toast visible for ~6.5s, then hide it.
    const hideTimer = window.setTimeout(() => setBuyer(null), 6500);
    return hideTimer;
  }, []);
  useEffect(() => {
    let hideTimer: number | undefined;
    let cycleTimer: number | undefined;
    const scheduleNext = (delay: number) => {
      cycleTimer = window.setTimeout(() => {
        hideTimer = showNext();
        // Next appearance in a random 40–60s window.
        const nextDelay = (Math.floor(Math.random() * 21) + 40) * 1000;
        scheduleNext(nextDelay);
      }, delay);
    };
    // First notification shortly after load.
    scheduleNext(5000);
    return () => {
      if (hideTimer) window.clearTimeout(hideTimer);
      if (cycleTimer) window.clearTimeout(cycleTimer);
    };
  }, [showNext]);
  return <div className="fixed bottom-4 left-4 z-50 max-w-[calc(100vw-2rem)] sm:max-w-sm" aria-live="polite" data-id="element-4">
      <AnimatePresence data-id="element-5">
        {buyer && <motion.div key={`${buyer.name}-${buyer.city}`} initial={{
        opacity: 0,
        y: 24,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 24,
        scale: 0.96
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="flex items-start gap-3 bg-card rounded-2xl shadow-soft border border-gray-100 p-4 pr-9 relative" data-id="element-6">
            <div className="bg-secondary/15 p-2 rounded-full flex-shrink-0" data-id="element-7">
              <BadgeCheck className="w-6 h-6 text-secondary" data-id="element-8" />
            </div>
            <div className="min-w-0" data-id="element-9">
              <p className="text-sm text-title font-medium leading-snug" data-id="element-10">
                <span className="font-heading font-bold" data-id="element-11">{buyer.name}</span> de{' '}
                {buyer.city} acaba de comprar {buyer.offer}.
              </p>
              <div className="flex items-center gap-1.5 mt-1.5" data-id="element-12">
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-secondary" data-id="element-13">
                  <BadgeCheck className="w-3.5 h-3.5" data-id="element-14" />
                  Compra verificada
                </span>
                <span className="text-xs text-body" data-id="element-15">
                  · hace {buyer.minutesAgo} min
                </span>
              </div>
            </div>
            <button onClick={() => setBuyer(null)} aria-label="Cerrar notificación" className="absolute top-2 right-2 text-gray-300 hover:text-body transition-colors" data-id="element-16">
              <X className="w-4 h-4" data-id="element-17" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}